<template>
  <div>
    <b-modal
      id="payments-breakdown-modal"
      ref="payments-breakdown-modal"
      centered
      title="CSV File"
      :ok-title="$t('DEBTIA_POST')"
      :no-close-on-backdrop="true"
      hide-footer
      cancel-variant="outline-secondary"
      size="xl"
    >
      <b-overlay
        id="overlay-background"
        :show="loader"
        variant="dark"
        opacity="0.85"
        blur="2px"
        rounded="sm"
        spinner-variant="primary"
        spinner-medium
      >
        <b-alert
          v-if="errorMsg"
          variant="warning"
          show
        >
          <h4 class="alert-heading">
            {{ $t('DEBITA_ERROR') }}
          </h4>
          <div class="alert-body">
            <span>{{ errorMsg }}</span>
          </div>
        </b-alert>
        <!-- Row Loop -->
        <b-row>

          <!-- button -->
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="float-right mb-1 w-15"
            block
            @click="postFile"
          >
            {{ $t('DEBITA_POST') }}
          </b-button>
          <b-table
            ref="refUserListTable"
            class="position-relative"
            :items="entries"
            responsive
            :fields="tableColumns"
            primary-key="id"
            show-empty
            empty-text="No matching records found"
            :tbody-tr-class="rowClass"
          >
            <template #cell(#)="data">
              {{ data.index + 1 }}
            </template>
            <template #cell(account)="data">
              <!-- Input -->
              <b-form-group>
                <v-select
                  v-model="data.item.paymentAccountType"
                  class="payments-account-select"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="paymentAccountTypes"
                  :reduce="val => val.value"
                  :clearable="false"
                  append-to-body
                  :input-id="`payment-account-type-${data.index}`"
                />
              </b-form-group>
              <div
                v-if="data.item.paymentAccountType === 'case'"
                class="case-combo-box"
              >
                <b-form-input
                  v-model="data.item.searchQuery"
                  class="caseinput"
                  placeholder="Search"
                  @keyup="toggleDropdown(data.item.searchQuery, data.item)"
                  @focusout="hideDropdown(data.item)"
                />
                <div
                  v-if="data.item.isDropdownVisible"
                  class="casedropdown"
                >
                  <div
                    v-if="(data.item.accountOptions).length < 1"
                    class="caseoption"
                  >
                    {{ "No Record..." }}
                  </div>
                  <b-overlay
                    :show="data.item.recordSearch"
                    rounded="lg"
                    opacity="0.6"
                  >

                    <div
                      v-for="(option, index) in data.item.accountOptions"
                      :key="index"
                      class="caseoption"
                      @click="selectOption(data.item, option.label)"
                    >
                      {{ option.label }}
                    </div>
                  </b-overlay></div>
              </div>
              <small
                v-if="data.item.account === 'SKAT RETUR' && (data.item.is_court_fee && data.item.is_court_fee !== '0')"
                class="text-danger"
              >*Both court fee and skat retur can not be selected together.</small>
              <div
                v-if="data.item.account === 'Invoice'"
                class="case-combo-box"
              >
                <b-form-input
                  v-model="data.item.bc_invoice_id"
                  class="invoiceInput"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  placeholder="Invoice Id"
                  @input="getInvoiceDetails(data.index, data.item.bc_invoice_id)"
                />
                <b-alert
                  v-if="data.item.account === 'Invoice' && data.item.bc_invoice_id && data.item.is_invoice_exist === false"
                  variant="warning"
                  class="mt-1"
                  show
                >
                  <div class="alert-body">
                    <span>{{ $t('DEBITA_INVOICE_NOT_FOUND') }}</span>
                  </div>
                </b-alert>
              </div>
            </template>
            <template #cell(is_court_fee)="data">
              <b-form-checkbox
                v-model="data.item.is_court_fee"
                :disabled="data.item.account === 'Invoice'"
              />
            </template>
            <template #cell(should_include)="data">
              <b-form-checkbox
                v-model="data.item.should_include"
              />
            </template>
          </b-table>
        </b-row>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BTable,
  BFormGroup,
  BFormInput,
  BButton,
  BOverlay,
  BFormCheckbox,
  // BInputGroupAppend,
} from 'bootstrap-vue'
// import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
// import { required } from '@validations'
import axios from '@axios'
import vSelect from 'vue-select'
// import { ValidationProvider, ValidationObserver } from 'vee-validate'
// import VuePerfectScrollbar from 'vue-perfect-scrollbar'
// import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import constants from '@/app-configs/constants'
import i18n from '@/libs/i18n'

export default {
  components: {
    vSelect,
    BFormGroup,
    BOverlay,
    BRow,
    BButton,
    BFormInput,
    BFormCheckbox,
    BTable,
  },
  directives: {
    Ripple,
  },
  props: {
    entries: {
      type: Array,
      required: true,
    },
    getList: {
      type: Function,
      required: true,
    },
    clientId: {
      type: String,
      required: true,
    },
    secretId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loader: false,
      client_id: this.clientId,
      secret_id: this.secretId,
      busy: false,
      paymentsList: [],
      tableColumns: [
        { key: '#', sortable: true, label: '#' },
        {
          key: 'value_date', sortable: true, label: i18n.t('DEBTIA_DATE'), class: 'text-center',
        },
        {
          key: 'text', sortable: true, label: i18n.t('DEBTIA_TEXT'), class: 'text-center',
        },
        {
          key: 'account', sortable: false, label: i18n.t('DEBTIA_ACCOUNT'), class: 'w-100 pr-0 pl-0',
        },
        {
          key: 'is_court_fee', label: i18n.t('DEBTIA_COURT_FEE'),
        },
        { key: 'amount', label: i18n.t('DEBTIA_AMOUNT'), class: 'text-center' },
        { key: 'should_include', label: i18n.t('DEBTIA_INCLUDE') },
      ],
      constants,
      stopPosting: false,
      errorMsg: '',
      //  dropdownWidth: 0,
      paymentAccountTypes: [
        {
          label: 'Case', value: 'case',
        },
        {
          label: 'Skat Retur', value: 'SKAT RETUR',
        },
        {
          label: 'Invoice', value: 'Invoice',
        },
      ],
    }
  },
  watch: {
  },
  created() {
  },
  methods: {
    hideDropdown(item) {
      setTimeout(() => {
        const filteritem = item.accountOptions.filter(element => element.label === item.searchQuery)
        // eslint-disable-next-line no-param-reassign
        item.recordSearch = false
        // eslint-disable-next-line no-param-reassign
        item.isDropdownVisible = false
        // eslint-disable-next-line no-param-reassign
        if (filteritem.length === 0) {
          // eslint-disable-next-line no-param-reassign
          item.searchQuery = null
        }
      }, 500)
    },
    async toggleDropdown(query, item) {
      if (query.length > 3) {
        // const inputWidth = document.querySelector('.case-combo-box input').offsetWidth
        // this.dropdownWidth = inputWidth
        // eslint-disable-next-line no-param-reassign
        item.recordSearch = true
        // eslint-disable-next-line no-param-reassign
        item.isDropdownVisible = true
        const cases = await axios.get(`${constants.caseServerBaseUrl}debtiaapp/debt-cases-dropdown?searchQuery=${query}`, {
          headers: {
            secret: this.secretId,
            clientId: this.clientId,
          },
        })
        // this.loader = false
        if (cases.status) {
          // eslint-disable-next-line no-param-reassign
          item.accountOptions = cases.data.data
          // eslint-disable-next-line no-param-reassign
          item.recordSearch = false
        }
      }
      if (query.length === 0) {
        // eslint-disable-next-line no-param-reassign
        item.accountOptions = []
        // eslint-disable-next-line no-param-reassign
        item.isDropdownVisible = false
      }
    },
    selectOption(item, option) {
      // eslint-disable-next-line no-param-reassign
      item.recordSearch = false
      // eslint-disable-next-line no-param-reassign
      item.isDropdownVisible = false
      // eslint-disable-next-line no-param-reassign
      item.searchQuery = option
    },
    postFile() {
      this.stopPosting = false
      for (const element of this.entries) {
        if (element.is_court_fee && (element.account === 'SKAT RETUR' || element.account === 'Invoice')) {
          this.stopPosting = true
          break
        }
      }
      if (this.stopPosting) {
        this.showAlert()
      } else {
        this.confirmText()
      }
    },
    confirmText() {
      const mainObject = {
        payment_entries: [],
      }
      let isNumberOfIncludeFalse = 0
      this.entries.forEach(element => {
        let elementAccount = element.searchQuery
        if (element.paymentAccountType === 'SKAT RETUR') {
          elementAccount = 'SKAT RETUR'
        }
        const obj = {
          payment_file_id: element.payment_file_id,
          value_date: element.value_date,
          text: element.text,
          account: elementAccount,
          is_court_fee: element.is_court_fee,
          should_include: element.should_include,
          amount: element.amount,
          currency: element.currency || '',
          id: element.id,
        }
        if (!element.should_include) {
          isNumberOfIncludeFalse += 1
        }
        mainObject.payment_entries.push(obj)
      })
      this.$swal({
        title: 'Are you sure?',
        text: `There are ${isNumberOfIncludeFalse} number of rows where include is false`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(async result => {
        if (result.value) {
          this.loader = true
          await axios.post('/payment-interface/files/submit', mainObject).then(response => {
            if (response.status) {
              this.$refs['payments-breakdown-modal'].hide()
              this.loader = false
              this.getList()
            }
          }).catch(err => {
            if (err.response.data.message) {
              const msg = typeof err.response.data.message === 'string' ? err.response.data.message : err.response.data.message[0]
              this.errorMsg = msg
              this.alertErrors.push(err.response.data.message)
            }
          })
        }
      })
    },
    showAlert() {
      this.$swal({
        title: 'Error!',
        text: 'There are some issues with some of the rows.',
        icon: 'warning',
        customClass: {
          confirmButton: 'btn btn-primary',
        },
        buttonsStyling: false,
      })
    },
    rowClass(item) {
      if (!item) return undefined
      const redColorClass = 'table-danger'
      const yellowColorClass = 'table-tableYellowColor'
      // eslint-disable-next-line consistent-return
      if (!item.account) {
        return yellowColorClass
      }
      const classes = []

      if (item.account === 'SKAT RETUR' && item.is_court_fee === true) {
        return redColorClass
      }
      if (item.isDropdownVisible) classes.push('row-dropdown-open')
      return classes.length ? classes.join(' ') : undefined
    },
    showModal() {
      this.$refs['payments-breakdown-modal'].show()
    },
    getAmountTruthy(amount) {
      const newString = String(amount)
      return newString.includes('-')
    },
    getInvoiceDetails(index, bcInvoiceId) {
      clearTimeout(this.invoiceSearchTimer)
      const entry = this.entries[index]
      if (!bcInvoiceId) {
        this.$set(entry, 'is_invoice_exist', false)
        return
      }
      this.invoiceSearchTimer = setTimeout(async () => {
        entry.account = 'Invoice'
        try {
          const records = await axios.get(`/invoice-system/invoice/search?q=${bcInvoiceId}`)
          if (records.status) {
            this.$set(entry, 'is_invoice_exist', !!records.data.data)
          }
        } catch (error) {
          this.$set(entry, 'is_invoice_exist', false)
        }
      }, 500)
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
.custom-control.custom-checkbox {
    width: fit-content;
    margin-left: 50%;
}

.payments-account-select {
  background-color: white;
}

.w-15 {
  width: 15%;
}

.table-tableYellowColor {
  background-color: #f5f5b7;
}

.bg-white {
  background-color: white;
}

.case-combo-box {
  //  position: relative;
  // display: inline-block;
  display: block;
  width: 100%;
}

.caseinput {
  padding: 5px;
  width: 200px;
  border: 1px solid #ccc;
  width: 100%;
}

.casedropdown {
  // position: absolute;
  // top: 100%;
  // left: 0;
  width: 100%;
  border: 1px solid #ccc;
  // border-top: none;
  background-color: #fff;
  max-height: 200px;
  overflow-y: auto;
  border-radius: 5px;
  //  z-index: 1000; /* Set a higher z-index value */
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  margin-top: 5px;
}

tr.row-dropdown-open td {
  overflow: visible;
  vertical-align: top;
}

.caseoption {
  padding: 5px;
  cursor: pointer;
  color: #333; /* Dark grey font */
}

.caseoption:hover {
  background-color: #f0f0f0;
}

.extra-length {
  height: 50px !important;
}
</style>
